












































































































































































































































import { Component, Watch, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';

import { FRONT_CHAT_API_URL } from 'frontend/constants';
import {
  User,
  ExperimentGetter,
  MobileGetter,
  StreakCalendarDay,
  StreakNotificationType,
  FeatureSwitches,
} from 'frontend/types';
import { getJson } from 'frontend/utils/ajax';

import NotificationsDrawer from 'frontend/components/header/NotificationsDrawer.vue';
import PremiumButton from 'frontend/components/header/PremiumButton.vue';
import LoginButton from 'frontend/components/header/LoginButton.vue';
import SignupButton from 'frontend/components/header/SignupButton.vue';
import StreakButton from 'frontend/components/header/StreakButton.vue';
import StreakPopup from 'frontend/components/header/StreakPopup.vue';
import StreakButtonWrapper from 'frontend/components/StreakButtonWrapper.vue';
import MenuIcon from 'frontend/components/icons/MenuIcon.vue';
import { isActuallyNux } from 'frontend/utils/nux';
import NavbarIcon from 'frontend/components/icons/NavbarIcon.vue';
import retool from 'frontend/utils/retool';

import {
  getOverriddenHeaderButtonText,
  getOverridenHeaderButtonAxId,
  getOverriddenHeaderButtonTextMobile,
  shouldOverrideHeaderButton,
  userCanRefer,
} from 'frontend/utils/referral';

@Component({
  components: {
    NavbarIcon,
    NotificationsDrawer,
    PremiumButton,
    StreakButtonWrapper,
    StreakButton,
    LoginButton,
    SignupButton,
    StreakPopup,
    MenuIcon,
    FrontChat: (resolve, reject) => {
      const script = document.createElement('script');
      script.onload = () => {
        // tslint:disable-next-line:space-in-parens
        import(/* webpackChunkName: 'FrontChat' */ './FrontChat.vue').then(module =>
          resolve(module.default),
        );
      };
      script.type = 'text/javascript';
      script.async = true;
      script.src = FRONT_CHAT_API_URL;
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    },
  },
  computed: {
    ...mapGetters('pageHeader', ['isMobile']),
    ...mapGetters('globals', ['featureSwitches', 'inExperiment']),
    ...mapGetters('response', ['debug']),
    ...mapState('globals', ['user']),
    ...mapState('streakPopup', ['streakPopupVisible', 'streakNotificationType']),
  },
})
export default class HeaderLinks extends Vue {
  private readonly featureSwitches!: FeatureSwitches;
  private readonly debug!: boolean;
  private user!: User;
  private isMobile!: MobileGetter;
  private showSettingsDropdown = false;
  private notificationsLoaded = false;
  private oldPageTitle = '';
  private notificationCount: number = 0;
  private streakPopupVisible!: boolean;
  private streakNotificationType!: StreakNotificationType;
  private inExperiment!: ExperimentGetter;

  private get retoolAuthorsDashboard(): string {
    return retool.authorsDashboard;
  }

  private get mobile(): boolean {
    return this.isMobile(this.$mq);
  }

  private get isActuallyNux() {
    return isActuallyNux(this.user, this.$route);
  }

  private get premiumButtonAxId() {
    if (this.shouldOverrideHeaderButtonForReferralOrGift) {
      return getOverridenHeaderButtonAxId();
    }
    if (this.user.has_current_trial_option) {
      return 'clicked_b2_site_header_free_trial';
    }
    if (this.isMobile(this.$mq)) {
      return 'clicked_b2_mobile_header';
    }
    return 'clicked_b2_from_homepage';
  }

  private get shouldOverrideHeaderButtonForReferralOrGift() {
    return shouldOverrideHeaderButton(this.user);
  }

  private get premiumButtonText() {
    if (this.shouldOverrideHeaderButtonForReferralOrGift) {
      if (this.isMobile(this.$mq)) {
        return getOverriddenHeaderButtonTextMobile();
      }
      return getOverriddenHeaderButtonText();
    }
    if (this.user.has_current_trial_option) {
      return 'Start trial';
    }
    return this.isMobile(this.$mq) ? 'Premium' : 'Go Premium';
  }

  private get currentStreak(): number {
    return this.user.streak.current_streak_length;
  }

  private get problemsSolvedInCurrentStreak(): number {
    return this.user.streak.problems_attempted_today;
  }

  private get calendar(): StreakCalendarDay[] {
    return this.user.streak.calendar;
  }

  private get problemsRequiredPerDay(): number {
    return this.user.streak.problems_required_today;
  }

  private get canIncreaseStreak(): boolean {
    return this.user.streak.can_increase_streak;
  }

  private handleCloseSettingsDropdown() {
    this.showSettingsDropdown = false;
    document.body.removeEventListener('click', this.handleCloseSettingsDropdown);
  }

  private handleShowSettingsDropdown() {
    if (!this.showSettingsDropdown) {
      this.showSettingsDropdown = true;
      // This timeout prevents the menu from immediately closing
      setTimeout(() => {
        document.body.addEventListener('click', this.handleCloseSettingsDropdown);
      }, 1);
    }
  }

  private init() {
    this.oldPageTitle = document.title;
    this.updateCount(this.user.num_displayable_unread_notifications);

    if (!this.debug) {
      this.setupNotificationPolling();
    }
  }

  private setupNotificationPolling(): void {
    let counter = 0;
    const f = (x: number) => {
      // 100x^2 + 500x + 5000
      return 100 * Math.pow(x, 2) + 500 * x + 5000;
    };
    const poll = () => {
      let lastNotifCountTimeout: any;
      getJson(
        this.$router.resolve({
          name: 'notifications_get_updated_unread_count',
        }).href,
      ).then(response => {
        if ('num_notifications' in response) {
          this.updateCount(response.num_notifications);
        } else if ('logged_out' in response) {
          clearTimeout(lastNotifCountTimeout);
        }
      });
      lastNotifCountTimeout = setTimeout(poll, f(counter));
      counter += 1;
    };
    poll();
  }

  private mounted() {
    // If there are unread notifications, we stick the unread count on the front of the page
    // title. This delay is necessary to ensure that the final page title has been set by
    // vue-meta before we add the notifications count.
    setTimeout(() => this.init(), 100);
  }

  private destroyed() {
    this.handleCloseSettingsDropdown();
  }

  private updatePageTitle() {
    if (this.notificationCount) {
      document.title = `(${this.notificationCount}) ${this.oldPageTitle}`;
    } else {
      document.title = this.oldPageTitle;
    }
  }

  private updateCount(newNotificationCount: number | undefined | null) {
    if (
      newNotificationCount !== null &&
      newNotificationCount !== undefined &&
      newNotificationCount !== this.notificationCount
    ) {
      this.notificationCount = newNotificationCount;
      this.updatePageTitle();
    }
  }

  private get moderatorOrStaff() {
    return (
      (this.user.is_discussions_moderator || this.user.is_staff) && !this.user.hide_admin_features
    );
  }

  @Watch('showSettingsDropdown')
  private getNotifications(val: boolean, oldVal: boolean) {
    if (val && val !== oldVal) {
      this.$store.dispatch('notificationsDrawer/loadNotifications').then(() => {
        this.notificationsLoaded = true;
      });
    } else if (!val && val !== oldVal) {
      this.notificationsLoaded = false;
    }
  }

  private get canRefer(): boolean {
    return userCanRefer(this.user);
  }

  private get isUserHome(): boolean {
    return this.$route.name === 'userhome';
  }

  private get showNavbarIcons(): boolean {
    return !this.isMobile(this.$mq);
  }
}
